import type { ScreeningAnswerViewModel } from "../../screening-question/types";
import type { MockApplicantData } from "../candidate-pool.types";

const workEligibilityQuestion: Omit<ScreeningAnswerViewModel, "actualBooleanAnswer"> = {
  screeningQuestionId: "sq-work-eligibility-ca",
  version: 1,
  code: "WORK_ELIGIBILITY_CANADA",
  questions: {
    "en-CA": "Do you have the legal right to work in Canada?",
    "fr-CA": "Avez-vous le droit légal de travailler au Canada?",
  },
  idealBooleanAnswer: true,
  isActiveOnJobPost: true,
};

const eligibleAnswer: ScreeningAnswerViewModel[] = [
  { ...workEligibilityQuestion, actualBooleanAnswer: true },
];
const ineligibleAnswer: ScreeningAnswerViewModel[] = [
  { ...workEligibilityQuestion, actualBooleanAnswer: false },
];

// CAND-004, 006, 009, 012 intentionally have no screeningAnswers - covers the
// "no answer" render branch in demo mode. Don't "fix" by adding answers.

export const initialCandidates: MockApplicantData[] = [
  {
    applicationId: "CAND-001",
    name: "Ava Thompson",
    firstName: "Ava",
    lastName: "Thompson",
    email: "ava.thompson@example.com",
    phoneNumber: "6045550101",
    linkedIn: "https://linkedin.com/in/ava-thompson",
    country: "Canada",
    subdivision: "BC",
    city: "Vancouver",
    appliedAt: "2025-01-04",
    source: "INDEED",
    match: 80,
    status: "IN_PIPELINE",
    pipelineStage: "RECRUITER_SCREENING",
    stageEnteredAt: "2025-01-05T16:00:00.000Z",
    accessIds: ["u1", "u2", "u3", "u4"],
    jobPostId: "j1",
    screeningAnswers: eligibleAnswer,
  },
  {
    applicationId: "CAND-002",
    name: "Liam Rodriguez",
    firstName: "Liam",
    lastName: "Rodriguez",
    email: "liam.rodriguez@example.com",
    phoneNumber: "4165550102",
    linkedIn: "https://linkedin.com/in/liam-rodriguez",
    country: "Canada",
    subdivision: "ON",
    city: "Toronto",
    appliedAt: "2025-01-06",
    source: "REFERRAL",
    referralEmployeeName: "Sarah Lee",
    match: 91,
    status: "IN_PIPELINE",
    pipelineStage: "INTERVIEW",
    stageEnteredAt: "2025-01-08T16:00:00.000Z",
    scheduledDate: "2025-01-12",
    accessIds: ["u5", "u6"],
    jobPostId: "j2",
    screeningAnswers: ineligibleAnswer,
  },
  {
    applicationId: "CAND-003",
    name: "Mia Johnson",
    firstName: "Mia",
    lastName: "Johnson",
    email: "mia.johnson@example.com",
    phoneNumber: "4035550103",
    linkedIn: "https://linkedin.com/in/mia-johnson",
    country: "Canada",
    subdivision: "AB",
    city: "Calgary",
    appliedAt: "2025-01-02",
    source: "INTERNAL",
    match: 74,
    status: "APPLIED",
    pipelineStage: null,
    stageEnteredAt: "2025-01-02T16:00:00.000Z",
    accessIds: ["u7"],
    jobPostId: "j3",
    screeningAnswers: eligibleAnswer,
  },
  {
    applicationId: "CAND-004",
    name: "Noah Patel",
    firstName: "Noah",
    lastName: "Patel",
    email: "noah.patel@example.com",
    phoneNumber: "5145550104",
    linkedIn: "https://linkedin.com/in/noah-patel",
    country: "Canada",
    subdivision: "QC",
    city: "Montreal",
    appliedAt: "2025-01-07",
    source: "INDEED",
    match: 63,
    status: "REJECTED",
    pipelineStage: null,
    stageEnteredAt: "2025-01-10T16:00:00.000Z",
    accessIds: [],
    jobPostId: "j4",
  },
  {
    applicationId: "CAND-005",
    name: "Emma Wright",
    firstName: "Emma",
    lastName: "Wright",
    email: "emma.wright@example.com",
    phoneNumber: "2505550105",
    linkedIn: "https://linkedin.com/in/emma-wright",
    country: "Canada",
    subdivision: "BC",
    city: "Victoria",
    appliedAt: "2025-01-05",
    source: "REFERRAL",
    referralEmployeeName: "John Carter",
    match: 90,
    status: "IN_PIPELINE",
    pipelineStage: "OFFER_EXTENDED",
    stageEnteredAt: "2025-01-09T16:00:00.000Z",
    accessIds: ["u8"],
    jobPostId: "j5",
    screeningAnswers: eligibleAnswer,
  },
  {
    applicationId: "CAND-006",
    name: "Oliver Green",
    firstName: "Oliver",
    lastName: "Green",
    email: "oliver.green@example.com",
    phoneNumber: "6135550106",
    linkedIn: "https://linkedin.com/in/oliver-green",
    country: "Canada",
    subdivision: "ON",
    city: "Ottawa",
    appliedAt: "2025-01-09",
    source: "INTERNAL",
    match: 52,
    status: "WITHDRAWN",
    pipelineStage: null,
    stageEnteredAt: "2025-01-11T16:00:00.000Z",
    accessIds: ["u9"],
    jobPostId: "j6",
  },
  {
    applicationId: "CAND-007",
    name: "Sophia Kim",
    firstName: "Sophia",
    lastName: "Kim",
    email: "sophia.kim@example.com",
    phoneNumber: "5195550107",
    linkedIn: "https://linkedin.com/in/sophia-kim",
    country: "Canada",
    subdivision: "ON",
    city: "Kitchener",
    appliedAt: "2025-01-03",
    source: "REFERRAL",
    referralEmployeeName: "Kevin Tran",
    match: 97,
    status: "IN_PIPELINE",
    pipelineStage: "INTERVIEW",
    stageEnteredAt: "2025-01-06T16:00:00.000Z",
    scheduledDate: "2025-01-13",
    accessIds: ["u10"],
    jobPostId: "j7",
    screeningAnswers: eligibleAnswer,
  },
  {
    applicationId: "CAND-008",
    name: "Elijah Brown",
    firstName: "Elijah",
    lastName: "Brown",
    email: "elijah.brown@example.com",
    phoneNumber: "2505550108",
    linkedIn: "https://linkedin.com/in/elijah-brown",
    country: "Canada",
    subdivision: "BC",
    city: "Kelowna",
    appliedAt: "2025-01-08",
    source: "INDEED",
    match: 45,
    status: "APPLIED",
    pipelineStage: null,
    stageEnteredAt: "2025-01-08T16:00:00.000Z",
    accessIds: ["u11"],
    jobPostId: "j8",
    screeningAnswers: ineligibleAnswer,
  },
  {
    applicationId: "CAND-009",
    name: "Isabella Davis",
    firstName: "Isabella",
    lastName: "Davis",
    email: "isabella.davis@example.com",
    phoneNumber: "2045550109",
    linkedIn: "https://linkedin.com/in/isabella-davis",
    country: "Canada",
    subdivision: "MB",
    city: "Winnipeg",
    appliedAt: "2025-01-04",
    source: "INTERNAL",
    match: 68,
    status: "IN_PIPELINE",
    pipelineStage: "RECRUITER_SCREENING",
    stageEnteredAt: "2025-01-07T16:00:00.000Z",
    scheduledDate: "2025-01-11",
    accessIds: ["u12"],
    jobPostId: "j9",
  },
  {
    applicationId: "CAND-010",
    name: "James Wilson",
    firstName: "James",
    lastName: "Wilson",
    email: "james.wilson@example.com",
    phoneNumber: "7805550110",
    linkedIn: "https://linkedin.com/in/james-wilson",
    country: "Canada",
    subdivision: "AB",
    city: "Edmonton",
    appliedAt: "2025-01-10",
    source: "INDEED",
    match: 59,
    status: "REJECTED",
    pipelineStage: null,
    stageEnteredAt: "2025-01-12T16:00:00.000Z",
    accessIds: ["u13"],
    jobPostId: "j10",
    screeningAnswers: eligibleAnswer,
  },
  {
    applicationId: "CAND-011",
    name: "Charlotte Nguyen",
    firstName: "Charlotte",
    lastName: "Nguyen",
    email: "charlotte.nguyen@example.com",
    phoneNumber: "9025550111",
    linkedIn: "https://linkedin.com/in/charlotte-nguyen",
    country: "Canada",
    subdivision: "NS",
    city: "Halifax",
    appliedAt: "2025-01-01",
    source: "REFERRAL",
    referralEmployeeName: "Emily Park",
    match: 80,
    status: "IN_PIPELINE",
    pipelineStage: "OFFER_EXTENDED",
    stageEnteredAt: "2025-01-10T16:00:00.000Z",
    scheduledDate: "2025-01-16",
    accessIds: ["u14"],
    jobPostId: "j11",
    screeningAnswers: ineligibleAnswer,
  },
  {
    applicationId: "CAND-012",
    name: "Henry Carter",
    firstName: "Henry",
    lastName: "Carter",
    email: "henry.carter@example.com",
    phoneNumber: "5195550112",
    linkedIn: "https://linkedin.com/in/henry-carter",
    country: "Canada",
    subdivision: "ON",
    city: "Waterloo",
    appliedAt: "2025-01-03",
    source: "INTERNAL",
    match: 70,
    status: "APPLIED",
    pipelineStage: null,
    stageEnteredAt: "2025-01-03T16:00:00.000Z",
    accessIds: ["u15"],
    jobPostId: "j12",
  },
];
